import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Send } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import { aiInterviewsAPI } from '../lib/aiInterviewApi';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import InterviewsBoard from '../components/aiinterview/recruiter/InterviewsBoard';
import PlansTab from '../components/aiinterview/recruiter/PlansTab';
import CreatePlanDialog from '../components/aiinterview/recruiter/CreatePlanDialog';
import InviteDialog from '../components/aiinterview/recruiter/InviteDialog';
import { AccommodationsTab, IntegrityQueueTab } from '../components/aiinterview/recruiter/ReviewTabs';
import SettingsTab from '../components/aiinterview/recruiter/SettingsTab';
import LowCreditBanner from '../components/LowCreditBanner';
import InterviewsGlobalPage from './InterviewsGlobalPage';

const TABS = [['interviews', 'Interviews'], ['plans', 'Plans'], ['integrity', 'Integrity review'], ['accommodations', 'Accommodations'], ['settings', 'Settings & fairness'], ['legacy', 'Legacy']];

const Stats = ({ s }) => {
  const items = [
    ['Invited', (s.invited || 0) + (s.opened || 0) + (s.consented || 0)],
    ['In progress', (s.in_progress || 0) + (s.starting || 0) + (s.interrupted || 0), false, s.interrupted ? `incl. ${s.interrupted} interrupted` : null],
    ['Reports ready', s.report_ready || 0],
    ['Needs review', s.needs_review || 0, true],
    ['Accommodation requests', s.pending_accommodations || 0, true],
  ];
  return (
    <div className="iv-stats" data-testid="hub-stats">
      {items.map(([label, n, amber, note]) => {
        const id = label.toLowerCase().replace(/\s+/g, '-');
        return (
          <div key={label} className={`iv-stat ${amber && n ? 'is-amber' : ''}`} data-testid={`hub-stat-${id}`}>
            <b>{n}</b><span>{label}</span>
            {note && <em className="iv-stat-note" data-testid={`hub-stat-${id}-note`}>{note}</em>}
          </div>
        );
      })}
    </div>
  );
};

export default function AIInterviewsHubPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'interviews';
  const [stats, setStats] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [invite, setInvite] = useState({ open: false, plan: null });
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStats = useCallback(() => aiInterviewsAPI.stats().then(({ data }) => setStats(data)).catch(() => {}), []);
  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);
  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="iv iv-rpage" data-testid="ai-interviews-hub">
      <div className="iv-rhead">
        <div>
          <div className="iv-eyebrow">AI recommends, humans decide</div>
          <h1 className="iv-rtitle">AI Interviews</h1>
          <p className="iv-sub" style={{ maxWidth: 640 }}>Adaptive 30-minute voice + video interviews built from each candidate's résumé and the JD, with evidence-linked reports and human integrity review.</p>
        </div>
        <div className="iv-row">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => setCreateOpen(true)} data-testid="create-plan-btn"><Plus size={16} /> Create Interview Plan</button>
          <button type="button" className="iv-btn iv-btn-cta" onClick={() => setInvite({ open: true, plan: null })} data-testid="invite-candidates-btn"><Send size={16} /> Invite Candidates</button>
        </div>
      </div>
      <LowCreditBanner />
      <Stats s={stats} />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="flex-wrap h-auto" data-testid="hub-tabs">
          {TABS.map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`hub-tab-${k}`}>
              {l}{k === 'integrity' && stats.needs_review ? ` (${stats.needs_review})` : ''}{k === 'accommodations' && stats.pending_accommodations ? ` (${stats.pending_accommodations})` : ''}
            </TabsTrigger>
          ))}
        </TabsList>
        <div style={{ marginTop: '1rem' }}>
          <TabsContent value="interviews"><InterviewsBoard refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="plans"><PlansTab refreshKey={refreshKey} onCreate={() => setCreateOpen(true)} onInvite={(plan) => setInvite({ open: true, plan })} /></TabsContent>
          <TabsContent value="integrity"><IntegrityQueueTab refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="accommodations"><AccommodationsTab refreshKey={refreshKey} onChanged={loadStats} /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="legacy">
            <div className="iv-note" style={{ marginBottom: '1rem' }} data-testid="legacy-note">Interviews created with the previous question-list module. They stay viewable here; new interviews use plans.</div>
            <InterviewsGlobalPage embedded />
          </TabsContent>
        </div>
      </Tabs>
      <CreatePlanDialog open={createOpen} onOpenChange={setCreateOpen} />
      <InviteDialog open={invite.open} presetPlan={invite.plan} onOpenChange={(o) => setInvite((x) => ({ ...x, open: o }))} onSent={refresh} />
    </div>
  );
}
